import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {TagCategory, tagCategorySelector, useGetTagCategoryQuery} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  TagCategoryForm,
  TagCategoryFormDefaultValues,
  TagCategoryFormValues
} from "../../components/form/TagCategoryForm/TagCategoryForm";
import {useTagCategoryMutation} from "../../hooks/useTagCategoryMutation";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {toFormDefaultsFromTagCategory} from "../../components/form/TagCategoryForm/useTagCategoryForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: TagCategoryFormDefaultValues
}

export const TagCategoryFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <TagCategoryFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const TagCategoryFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectTagCategory = useMemo(() => {
    return tagCategorySelector()
  }, [])

  const {
    currTagCategory,
    isLoading
  } = useGetTagCategoryQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currTagCategory: selectTagCategory(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: TagCategoryFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromTagCategory(currTagCategory);
  }, [currTagCategory, formDefaults]);

  const isEdit = useMemo(() => {
    return currTagCategory && !copy
  }, [currTagCategory, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("common.page.entity.tagcategory.form.title.edit", "Edit Tag Category", {ns: "common"});
    } else {
      return t("common.page.entity.tagcategory.form.title.new", "New Tag Category", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createTagCategory,
    updateTagCategory,
    isCreating,
    isUpdating,
  } = useTagCategoryMutation()

  const onTagCategorySubmit = useCallback((formValues: TagCategoryFormValues) => {
    const patch: Partial<TagCategory> = {
      label: formValues.label,
      description: formValues.description,
      doNotUse: formValues.doNotUse ?? false,
    }

    if (isEdit) {
      const profile: Partial<TagCategory> = {
        ...currTagCategory.asTypeValue(),
        ...patch,
      };
      updateTagCategory(profile, {onSuccess: () => navigate(-1)})
    } else {
      const profile: Partial<TagCategory> = {
        ...patch,
      };
      createTagCategory(profile, {onSuccess: () => navigate(-1)})
    }
  }, [isEdit, currTagCategory, updateTagCategory, navigate, createTagCategory]);

  return (
    <ProtectedPage
      entity={currTagCategory}
      permissions={[isEdit ? "tagcategory_edit" : "tagcategory_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <TagCategoryForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onTagCategorySubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

